// Componentes próprios no lugar de select e input month nativos. O valor vai num
// input hidden com o mesmo name, então servidor e autosave não mudam.
import { persistDraftNow } from './drafts';

const MONTHS_SHORT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const MONTHS_LONG = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

function closeOnOutside(root: HTMLElement, close: () => void): void {
  document.addEventListener('pointerdown', (event) => {
    if (event.target instanceof Node && !root.contains(event.target)) close();
  });
}

// Fecha o painel quando o foco sai do componente. Confere depois do tick: trocar de mês ou ano
// redesenha a grade e tira o botão focado do DOM antes de o foco ir para o botão novo.
function closeOnFocusLeave(root: HTMLElement, close: () => void): void {
  root.addEventListener('focusout', () => {
    window.setTimeout(() => {
      if (!root.contains(document.activeElement)) close();
    }, 0);
  });
}

function emitChange(field: HTMLInputElement): void {
  field.dispatchEvent(new Event('change', { bubbles: true }));
}

// Select: botão + listbox (aria-activedescendant) + hidden.
export function bindSelects(): void {
  document.querySelectorAll<HTMLElement>('[data-select]').forEach((root) => {
    if (root.dataset.bound === 'true') return;
    const trigger = root.querySelector<HTMLButtonElement>('button[aria-haspopup="listbox"]');
    const list = root.querySelector<HTMLUListElement>('[role="listbox"]');
    const hidden = root.querySelector<HTMLInputElement>('[data-select-value]');
    const label = root.querySelector<HTMLElement>('[data-select-label]');
    if (!trigger || !list || !hidden) return;
    root.dataset.bound = 'true';
    const options = Array.from(list.querySelectorAll<HTMLLIElement>('[role="option"]'));
    let active = -1;
    let typed = '';
    let typedAt = 0;

    const sync = (): void => {
      if (hidden.value === '' && options[0]) hidden.value = options[0].dataset.value ?? '';
      const selected = options.find((option) => option.dataset.value === hidden.value);
      options.forEach((option) => option.setAttribute('aria-selected', option === selected ? 'true' : 'false'));
      if (selected && label) label.textContent = selected.dataset.label ?? selected.textContent?.trim() ?? '';
    };
    const setActive = (index: number): void => {
      if (options.length === 0) return;
      active = Math.max(0, Math.min(options.length - 1, index));
      options.forEach((option, position) => option.classList.toggle('is-active', position === active));
      const current = options[active];
      if (!current) return;
      list.setAttribute('aria-activedescendant', current.id);
      current.scrollIntoView({ block: 'nearest' });
    };
    const open = (): void => {
      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      setActive(Math.max(0, options.findIndex((option) => option.dataset.value === hidden.value)));
      list.focus();
    };
    const close = (focusTrigger: boolean): void => {
      if (list.hidden) return;
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      list.removeAttribute('aria-activedescendant');
      if (focusTrigger) trigger.focus();
    };
    const choose = (index: number): void => {
      const option = options[index];
      if (!option) return;
      const changed = hidden.value !== option.dataset.value;
      hidden.value = option.dataset.value ?? '';
      sync();
      if (changed) emitChange(hidden);
      close(true);
    };

    trigger.addEventListener('click', () => (list.hidden ? open() : close(true)));
    trigger.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open();
      }
    });
    list.addEventListener('keydown', (event) => {
      const keys: Record<string, () => void> = {
        ArrowDown: () => setActive(active + 1),
        ArrowUp: () => setActive(active - 1),
        Home: () => setActive(0),
        End: () => setActive(options.length - 1),
        Enter: () => choose(active),
        ' ': () => choose(active),
        Escape: () => close(true),
      };
      const action = keys[event.key];
      if (action) {
        event.preventDefault();
        action();
      } else if (event.key === 'Tab') {
        close(false);
      } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // Busca por letra (lista longa, como a de UF): letras seguidas em até 700 ms somam.
        const now = Date.now();
        typed = (now - typedAt > 700 ? '' : typed) + event.key.toLowerCase();
        typedAt = now;
        const index = options.findIndex((option) => (option.textContent ?? '').trim().toLowerCase().startsWith(typed));
        if (index >= 0) setActive(index);
        event.preventDefault();
      }
    });
    list.addEventListener('focusout', (event) => {
      if (!(event.relatedTarget instanceof Node) || !root.contains(event.relatedTarget)) close(false);
    });
    options.forEach((option, index) => {
      option.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        choose(index);
      });
    });
    // Quem preenche o hidden por código (diálogo do tomador) dispara change para o rótulo acompanhar.
    hidden.addEventListener('change', sync);
    closeOnOutside(root, () => close(false));
    sync();
  });
}

// Competência: botão que abre um painel com o ano e os 12 meses.
export function bindMonthPickers(): void {
  document.querySelectorAll<HTMLElement>('[data-month-picker]').forEach((root) => {
    if (root.dataset.bound === 'true') return;
    const trigger = root.querySelector<HTMLButtonElement>('button[aria-haspopup="dialog"]');
    const panel = root.querySelector<HTMLElement>('[role="dialog"]');
    const grid = root.querySelector<HTMLElement>('[data-month-grid]');
    const yearLabel = root.querySelector<HTMLElement>('[data-month-year]');
    const hidden = root.querySelector<HTMLInputElement>('[data-month-value]');
    const label = root.querySelector<HTMLElement>('[data-month-label]');
    if (!trigger || !panel || !grid || !yearLabel || !hidden || !label) return;
    root.dataset.bound = 'true';

    const today = new Date();
    const parse = (): { year: number; month: number } | null => {
      const match = /^(\d{4})-(\d{2})$/.exec(hidden.value);
      return match ? { year: Number(match[1]), month: Number(match[2]) - 1 } : null;
    };
    let viewYear = parse()?.year ?? today.getFullYear();
    let focusIndex = parse()?.month ?? today.getMonth();

    const buttons = (): HTMLButtonElement[] => Array.from(grid.querySelectorAll<HTMLButtonElement>('button'));
    const render = (): void => {
      const selected = parse();
      yearLabel.textContent = String(viewYear);
      grid.replaceChildren(
        ...MONTHS_SHORT.map((name, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'month-picker__month';
          button.textContent = name;
          button.dataset.index = String(index);
          button.setAttribute('aria-label', `${MONTHS_LONG[index]} de ${viewYear}`);
          button.setAttribute('aria-pressed', selected?.year === viewYear && selected.month === index ? 'true' : 'false');
          if (today.getFullYear() === viewYear && today.getMonth() === index) button.classList.add('is-today');
          button.tabIndex = index === focusIndex ? 0 : -1;
          return button;
        }),
      );
    };
    const focusMonth = (index: number): void => {
      focusIndex = Math.max(0, Math.min(11, index));
      buttons().forEach((button, position) => (button.tabIndex = position === focusIndex ? 0 : -1));
      buttons()[focusIndex]?.focus();
    };
    const open = (): void => {
      const selected = parse();
      viewYear = selected?.year ?? today.getFullYear();
      focusIndex = selected?.month ?? today.getMonth();
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      render();
      focusMonth(focusIndex);
    };
    const close = (focusTrigger: boolean): void => {
      if (panel.hidden) return;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (focusTrigger) trigger.focus();
    };
    const shiftYear = (delta: number): void => {
      viewYear += delta;
      render();
    };

    trigger.addEventListener('click', () => (panel.hidden ? open() : close(true)));
    root.querySelector<HTMLButtonElement>('[data-month-prev]')?.addEventListener('click', () => shiftYear(-1));
    root.querySelector<HTMLButtonElement>('[data-month-next]')?.addEventListener('click', () => shiftYear(1));
    // Filtro (histórico): limpar volta ao rótulo original do servidor, que diz "todas".
    const placeholder = label.classList.contains('is-placeholder') ? label.textContent : 'Todas as competências';
    root.querySelector<HTMLButtonElement>('[data-month-clear]')?.addEventListener('click', () => {
      hidden.value = '';
      label.textContent = placeholder;
      label.classList.add('is-placeholder');
      emitChange(hidden);
      close(true);
    });
    grid.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>('button[data-index]') : null;
      if (!button) return;
      const index = Number(button.dataset.index);
      hidden.value = `${viewYear}-${String(index + 1).padStart(2, '0')}`;
      label.textContent = `${MONTHS_LONG[index]} de ${viewYear}`;
      label.classList.remove('is-placeholder');
      emitChange(hidden);
      close(true);
    });
    grid.addEventListener('keydown', (event) => {
      const moves: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -4, ArrowDown: 4 };
      const move = moves[event.key];
      if (move !== undefined) {
        event.preventDefault();
        focusMonth(focusIndex + move);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        focusMonth(event.key === 'Home' ? 0 : 11);
      } else if (event.key === 'PageUp' || event.key === 'PageDown') {
        event.preventDefault();
        shiftYear(event.key === 'PageUp' ? -1 : 1);
        focusMonth(focusIndex);
      }
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      }
    });
    closeOnFocusLeave(root, () => close(false));
    closeOnOutside(root, () => close(false));
  });
}

// Serviço: command que filtra o catálogo já renderizado e oferece cadastrar um novo.
export function bindServiceCombobox(): void {
  const root = document.querySelector<HTMLElement>('[data-service-combobox]');
  if (!root || root.dataset.bound === 'true') return;
  const input = root.querySelector<HTMLInputElement>('[role="combobox"]');
  const hidden = root.querySelector<HTMLInputElement>('[data-service-id]');
  const list = root.querySelector<HTMLUListElement>('[role="listbox"]');
  const empty = root.querySelector<HTMLElement>('[data-service-empty]');
  const status = root.querySelector<HTMLElement>('[data-service-status]');
  if (!input || !hidden || !list) return;
  root.dataset.bound = 'true';
  const issueForm = document.querySelector<HTMLFormElement>('#form-emitir');
  const options = Array.from(list.querySelectorAll<HTMLLIElement>('.combobox__option'));
  let active = -1;

  const announce = (text: string): void => {
    if (status) status.textContent = text;
  };
  const plain = (text: string): string => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
  const visible = (): HTMLLIElement[] => options.filter((option) => !option.hidden);
  const selectedLabel = (): string => options.find((option) => option.dataset.value === hidden.value)?.dataset.label ?? '';
  const filter = (): void => {
    const showAll = input.value.trim() === '' || (hidden.value !== '' && input.value === selectedLabel());
    const query = plain(input.value.trim());
    let found = 0;
    options.forEach((option) => {
      if (option.dataset.value === 'novo-servico') return;
      option.hidden = !showAll && !plain(option.dataset.label ?? '').includes(query);
      if (!option.hidden) found += 1;
    });
    if (empty) empty.hidden = found > 0;
    active = -1;
    input.removeAttribute('aria-activedescendant');
    options.forEach((option) => option.setAttribute('aria-selected', 'false'));
    announce(found === 1 ? '1 serviço encontrado.' : `${found} serviços encontrados.`);
  };
  const open = (): void => {
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };
  const close = (): void => {
    list.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
    active = -1;
  };
  const activate = (index: number): void => {
    const current = visible();
    if (current.length === 0) return;
    active = (index + current.length) % current.length;
    current.forEach((option, position) => option.setAttribute('aria-selected', position === active ? 'true' : 'false'));
    const option = current[active];
    if (!option) return;
    input.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  };
  // Cadastro novo vai para o catálogo; o rascunho salvo traz a nota de volta intacta.
  const createService = async (): Promise<void> => {
    close();
    if (!issueForm || !(await persistDraftNow(issueForm))) {
      announce('O rascunho precisa ser salvo antes de cadastrar o serviço. Use “Salvar rascunho” e tente de novo.');
      return;
    }
    const draft = issueForm.elements.namedItem('draft_id');
    if (!(draft instanceof HTMLInputElement) || draft.value === '') return;
    window.location.assign(`/servicos/novo?draft=${encodeURIComponent(draft.value)}`);
  };
  const choose = (option: HTMLLIElement): void => {
    if (option.dataset.value === 'novo-servico') {
      void createService();
      return;
    }
    hidden.value = option.dataset.value ?? '';
    input.value = option.dataset.label ?? '';
    emitChange(hidden);
    announce(`Serviço ${input.value} selecionado.`);
    close();
  };

  options.forEach((option) => {
    option.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      choose(option);
    });
  });
  input.addEventListener('input', () => {
    if (hidden.value !== '') {
      hidden.value = '';
      emitChange(hidden);
    }
    filter();
    open();
  });
  input.addEventListener('focus', () => {
    filter();
    open();
    // Serviço já escolhido: o texto fica selecionado e a nova busca substitui.
    if (hidden.value !== '') input.select();
  });
  input.addEventListener('click', () => {
    if (list.hidden) {
      filter();
      open();
    }
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (list.hidden) {
        filter();
        open();
      }
      activate(active + (event.key === 'ArrowDown' ? 1 : -1));
    } else if (event.key === 'Enter' && active >= 0) {
      event.preventDefault();
      const option = visible()[active];
      if (option) choose(option);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  });
  closeOnOutside(root, close);
}

// Data (relatórios): botão que abre um calendário do mês; o hidden guarda AAAA-MM-DD.
export function bindDatePickers(): void {
  document.querySelectorAll<HTMLElement>('[data-date-picker]').forEach((root) => {
    if (root.dataset.bound === 'true') return;
    const trigger = root.querySelector<HTMLButtonElement>('button[aria-haspopup="dialog"]');
    const panel = root.querySelector<HTMLElement>('[role="dialog"]');
    const grid = root.querySelector<HTMLElement>('[data-date-grid]');
    const title = root.querySelector<HTMLElement>('[data-date-title]');
    const hidden = root.querySelector<HTMLInputElement>('[data-date-value]');
    const label = root.querySelector<HTMLElement>('[data-date-label]');
    if (!trigger || !panel || !grid || !title || !hidden || !label) return;
    root.dataset.bound = 'true';

    const iso = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const parse = (): Date | null => {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(hidden.value);
      return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
    };
    let focusDate = parse() ?? new Date();

    const dayButtons = (): HTMLButtonElement[] => Array.from(grid.querySelectorAll<HTMLButtonElement>('button[data-date]'));
    const render = (): void => {
      const year = focusDate.getFullYear();
      const month = focusDate.getMonth();
      const selected = hidden.value;
      const today = iso(new Date());
      title.textContent = `${MONTHS_LONG[month]} de ${year}`;
      const lead = new Date(year, month, 1).getDay(); // domingo = 0
      const days = new Date(year, month + 1, 0).getDate();
      const cells: HTMLElement[] = [];
      for (let i = 0; i < lead; i++) {
        const blank = document.createElement('span');
        blank.setAttribute('aria-hidden', 'true');
        cells.push(blank);
      }
      for (let day = 1; day <= days; day++) {
        const value = iso(new Date(year, month, day));
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'month-picker__month date-picker__day';
        button.textContent = String(day);
        button.dataset.date = value;
        button.setAttribute('aria-label', `${day} de ${MONTHS_LONG[month]} de ${year}`);
        button.setAttribute('aria-pressed', value === selected ? 'true' : 'false');
        if (value === today) button.classList.add('is-today');
        button.tabIndex = day === focusDate.getDate() ? 0 : -1;
        cells.push(button);
      }
      grid.replaceChildren(...cells);
    };
    const focusOn = (next: Date): void => {
      const sameMonth = next.getMonth() === focusDate.getMonth() && next.getFullYear() === focusDate.getFullYear();
      focusDate = next;
      if (!sameMonth) render();
      dayButtons().forEach((button) => (button.tabIndex = button.dataset.date === iso(focusDate) ? 0 : -1));
      grid.querySelector<HTMLButtonElement>(`button[data-date="${iso(focusDate)}"]`)?.focus();
    };
    const shiftMonth = (delta: number): void => {
      const day = focusDate.getDate();
      const target = new Date(focusDate.getFullYear(), focusDate.getMonth() + delta, 1);
      const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
      focusDate = new Date(target.getFullYear(), target.getMonth(), Math.min(day, last));
      render();
    };
    const open = (): void => {
      focusDate = parse() ?? new Date();
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      render();
      focusOn(focusDate);
    };
    const close = (focusTrigger: boolean): void => {
      if (panel.hidden) return;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (focusTrigger) trigger.focus();
    };

    trigger.addEventListener('click', () => (panel.hidden ? open() : close(true)));
    root.querySelector<HTMLButtonElement>('[data-date-prev]')?.addEventListener('click', () => shiftMonth(-1));
    root.querySelector<HTMLButtonElement>('[data-date-next]')?.addEventListener('click', () => shiftMonth(1));
    grid.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest<HTMLButtonElement>('button[data-date]') : null;
      if (!button?.dataset.date) return;
      hidden.value = button.dataset.date;
      label.textContent = button.getAttribute('aria-label') ?? button.dataset.date;
      label.classList.remove('is-placeholder');
      emitChange(hidden);
      close(true);
    });
    grid.addEventListener('keydown', (event) => {
      const steps: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      const step = steps[event.key];
      if (step !== undefined) {
        event.preventDefault();
        focusOn(new Date(focusDate.getFullYear(), focusDate.getMonth(), focusDate.getDate() + step));
      } else if (event.key === 'PageUp' || event.key === 'PageDown') {
        event.preventDefault();
        shiftMonth(event.key === 'PageUp' ? -1 : 1);
        focusOn(focusDate);
      }
    });
    panel.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      }
    });
    closeOnFocusLeave(root, () => close(false));
    closeOnOutside(root, () => close(false));
  });
}

// Arquivo (lotes): o input file nativo fica escondido e acessível; a zona mostra o nome escolhido
// e aceita arrastar e soltar.
export function bindFilePickers(): void {
  document.querySelectorAll<HTMLElement>('[data-file-picker]').forEach((root) => {
    if (root.dataset.bound === 'true') return;
    const input = root.querySelector<HTMLInputElement>('input[type="file"]');
    const name = root.querySelector<HTMLElement>('[data-file-name]');
    const zone = root.querySelector<HTMLElement>('.file-picker__zone');
    if (!input || !name || !zone) return;
    root.dataset.bound = 'true';
    const empty = name.textContent ?? '';

    const sync = (): void => {
      const file = input.files?.[0];
      name.textContent = file ? file.name : empty;
      root.classList.toggle('has-file', Boolean(file));
    };
    input.addEventListener('change', sync);
    zone.addEventListener('dragover', (event) => {
      event.preventDefault();
      root.classList.add('is-dragging');
    });
    zone.addEventListener('dragleave', () => root.classList.remove('is-dragging'));
    zone.addEventListener('drop', (event) => {
      event.preventDefault();
      root.classList.remove('is-dragging');
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;
      input.files = files;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
    sync();
  });
}
